import React, { useState } from 'react';

import { gql } from '@apollo/client';
import { Trans, useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { formatDate, getLocalTimezone, guessIsoTimestamp } from '@modernloop/shared/datetime';
import {
  DateIcon,
  DiamondFilled,
  EditIcon,
  ReverseShadowIcon,
  ShadowIcon,
  TimeToTrainIcon,
} from '@modernloop/shared/icons';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { isFuture, isPast } from 'date-fns';

import { InterviewModuleMemberKeyDates_ModuleMemberFragment, TrainingStatus } from 'src/generated/mloop-graphql';

import EditKeyDatesModal from './Modals/EditKeyDatesModal';

type Fragments = {
  moduleMember: InterviewModuleMemberKeyDates_ModuleMemberFragment;
};

enum KeyDates {
  AddedToModule = 'ADDED_TO_MODULE',
  FirstShadow = 'FIRST_SHADOW',
  FirstReverseShadow = 'FIRST_REVERSE_SHADOW',
  FullyTrained = 'FULLY_TRAINED',
  TimeToTrain = 'TIME_TO_TRAIN',
}

const ListItemData = [
  {
    titleKey: 'interviewers.moduleMemberSidepanel.keyDates.addedToModule',
    key: KeyDates.AddedToModule,
    icon: <DateIcon />,
  },
  {
    titleKey: 'interviewers.moduleMemberSidepanel.keyDates.firstShadow',
    key: KeyDates.FirstShadow,
    icon: <ShadowIcon />,
  },
  {
    titleKey: 'interviewers.moduleMemberSidepanel.keyDates.firstReverseShadow',
    key: KeyDates.FirstReverseShadow,
    icon: <ReverseShadowIcon />,
  },
  {
    titleKey: 'interviewers.moduleMemberSidepanel.keyDates.fullyTrained',
    key: KeyDates.FullyTrained,
    icon: <DiamondFilled />,
    hasTooltip: false,
  },
  {
    titleKey: 'interviewers.moduleMemberSidepanel.keyDates.timeToTrain',
    key: KeyDates.TimeToTrain,
    icon: <TimeToTrainIcon />,
    hasTooltip: true,
  },
];
const DEFAULT_DATE = '1970-01-01T00:00:01.000Z';

const utilGetFormattedDate = ({
  date = '',
  requiredCount = 0,
  timeToTrain,
  type,
  status,
  t,
}: {
  date?: string;
  requiredCount?: number;
  timeToTrain?: number;
  type: KeyDates;
  status: TrainingStatus;
  t: (key: string, options?: Record<string, unknown>) => string;
}) => {
  if (date && date === DEFAULT_DATE) {
    return t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.keyDates.unknown');
  }

  if (type === KeyDates.AddedToModule) {
    return date
      ? formatDate(guessIsoTimestamp(date), getLocalTimezone(), 'MMM d, yyyy')
      : t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.keyDates.unknown');
  }

  // Show formatted date for first shadow ,fully trained and first reverse shadow
  if (date && isPast(new Date(date)) && type !== KeyDates.TimeToTrain) {
    return formatDate(guessIsoTimestamp(date), getLocalTimezone(), 'MMM d, yyyy');
  }
  if (type === KeyDates.FullyTrained) {
    if (status === TrainingStatus.Trained && !date) {
      return t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.keyDates.unknown');
    }
  }

  // Show current status for first shadow and first reverse shadow
  if (type !== KeyDates.TimeToTrain && type !== KeyDates.FullyTrained) {
    if (isFuture(new Date(date))) {
      return t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.keyDates.scheduled');
    }
    if (!date && requiredCount <= 0) {
      return t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.keyDates.notRequired');
    }
  }

  if (type === KeyDates.TimeToTrain) {
    if (status === TrainingStatus.Trained && !date) {
      return t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.keyDates.unknown');
    }
    if (date && timeToTrain === 0) {
      return t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.keyDates.unknown');
    }
    return date
      ? t('hiring-dashboard-interviewers:interviewers.moduleMember.timeToTrain', { count: timeToTrain || 1 })
      : '';
  }

  return '';
};

const InterviewModuleMemberKeyDates: FCWithFragments<Fragments> = ({ moduleMember }) => {
  const { t } = useTranslation();
  const [showEditKeyDatesModal, setShowEditKeyDatesModal] = useState(false);

  const getData = (type: KeyDates) => {
    switch (type) {
      case KeyDates.AddedToModule:
        return utilGetFormattedDate({
          date: moduleMember.createdAt,
          type: KeyDates.AddedToModule,
          status: moduleMember.status,
          t,
        });
      case KeyDates.FirstReverseShadow:
        return utilGetFormattedDate({
          requiredCount: moduleMember.reverseShadowsRequired,
          date: moduleMember.reverseShadowStartAt,
          type: KeyDates.FirstReverseShadow,
          status: moduleMember.status,
          t,
        });
      case KeyDates.FirstShadow:
        return utilGetFormattedDate({
          requiredCount: moduleMember.shadowsRequired,
          date: moduleMember.shadowStartAt,
          type: KeyDates.FirstShadow,
          status: moduleMember.status,
          t,
        });
      case KeyDates.FullyTrained:
        return utilGetFormattedDate({
          date: moduleMember.trainedAt,
          type: KeyDates.FullyTrained,
          status: moduleMember.status,
          t,
        });
      case KeyDates.TimeToTrain:
        return utilGetFormattedDate({
          timeToTrain: moduleMember.timeToTrain,
          date: moduleMember.trainedAt,
          type: KeyDates.TimeToTrain,
          status: moduleMember.status,
          t,
        });
      default:
        break;
    }

    return '';
  };

  return (
    <Accordion variant="outlined" disableGutters defaultExpanded>
      <AccordionSummary>
        <Typography variant="subtitle2">
          {t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.keyDates.title')}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="column">
          {ListItemData.map((item) => {
            const value = getData(item.key);
            const tooltip = item.hasTooltip ? (
              <Trans i18nKey="hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.keyDates.timeToTrainTooltip">
                Time to train is the number of days it takes to go from starting a module to becoming eligible to
                conduct an interview solo.
                <b>
                  {' '}
                  The start date for a module is determined by the earliest of the following dates: Added to module,
                  First shadow, or First reverse shadow.
                </b>{' '}
                You can edit dates via the module log or the edit button of the Key dates section.
              </Trans>
            ) : undefined;
            return (
              <Stack
                sx={
                  !value
                    ? {
                        opacity: '0.5',
                      }
                    : {}
                }
                key={item.key}
                direction="row"
                alignItems="center"
                py={1}
                justifyContent="space-between"
              >
                <Tooltip title={tooltip} sx={tooltip ? { cursor: 'pointer' } : undefined}>
                  <Stack direction="row" alignItems="center" columnGap={1}>
                    {item.icon}
                    <Typography variant="body2">{t(`hiring-dashboard-interviewers:${item.titleKey}`)}</Typography>
                  </Stack>
                </Tooltip>
                <Box textAlign="right">
                  <Typography variant="body2">{value}</Typography>
                </Box>
              </Stack>
            );
          })}

          <Box>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setShowEditKeyDatesModal(true)}
              sx={{ float: 'right' }}
            >
              {t('hiring-dashboard-common:common.edit')}
            </Button>
          </Box>
        </Stack>
      </AccordionDetails>

      {showEditKeyDatesModal && (
        <EditKeyDatesModal moduleMember={moduleMember} onClose={() => setShowEditKeyDatesModal(false)} />
      )}
    </Accordion>
  );
};

InterviewModuleMemberKeyDates.fragments = {
  moduleMember: gql`
    ${EditKeyDatesModal.fragments.moduleMember}
    fragment InterviewModuleMemberKeyDates_moduleMember on InterviewModuleMember {
      id
      interviewModuleId
      employeeId
      shadowStartAt
      reverseShadowStartAt
      employee {
        id
        fullName
        internalProfilePictureUrl
      }
      shadowsRequired
      reverseShadowsRequired
      interviewModule {
        id
        name
      }
      status
      createdAt
      trainedAt
      timeToTrain

      ...EditKeyDatesModal_moduleMember
    }
  `,
};

export default InterviewModuleMemberKeyDates;
